/**
 * THE ACCOUNTS RAIL ITEM (item 7) — a customer picker only.
 *
 * What this used to draw — holdings, owed, overdue — now lives on the
 * customer page's tabs (22 Sep), reached from `#/customers/{id}`. Opening a
 * customer's account by its old address, `#/account/{id}`, still works: it
 * redirects to the Overview tab. The Accounts rail item itself keeps only
 * the picker, because the rail needs somewhere to land before a customer is
 * chosen.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, type Session } from '../api.js';
import { useAsk, go } from '../hooks.js';
import { Card, Loading, Register } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('account')!.messages;

export function Account({ id }: { id: string | null; session: Session }) {
  useEffect(() => { if (id && /^\d+$/.test(id)) go(`#/customers/${id}?tab=overview`); }, [id]);
  if (id && /^\d+$/.test(id)) return <Page title="Customer account"><Loading what="the customer page" /></Page>;
  return <PickCustomer />;
}

function PickCustomer() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('customers.list', { ...(q.trim() ? { q: q.trim() } : {}), active: true, page, pageSize: 50 }), [q, page], MESSAGES);
  return (
    <Page title="Customer accounts" sub="Choose a customer to open their page — holdings, bills, payments and more.">
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Customer name" onChange={(e: { target: { value: string } }) => { setQ(e.target.value); setPage(1); }} />
        </div>
      </div>
      <Card>
        {asked.loading ? <Loading what="customers" /> : (<>
          <Register
            columns={[
              { key: 'name', head: 'Customer', cell: (r: { name: string }) => <strong>{r.name}</strong> },
              { key: 'gstin', head: 'GSTIN', cell: (r: { gstin: string | null }) => r.gstin ?? '—' },
            ]}
            rows={asked.data?.rows ?? []} empty="No customer matches that."
            onOpen={(r) => go(`#/customers/${r.customerId}?tab=overview`)} />
          {asked.data ? <Pager page={asked.data.page} onPage={setPage} /> : null}
        </>)}
      </Card>
    </Page>
  );
}
